import type { MessageDirection } from '@/constants';

// CloudEvent Protocol Types
export interface CloudEvent<T = unknown> {
  specversion: string;
  type: string;
  source: string;
  id: string;
  time: string;
  datacontenttype: string;
  subject?: string;
  data: T;
}

// CloudEvent Type Constants
export const CLOUD_EVENT_TYPES = {
  CONNECTION_ESTABLISHED: 'com.cuca-gazelle.connection.established',
  GENERATING_RESPONSE: 'com.cuca-gazelle.generating-response',
  RESPONSE: 'com.cuca-gazelle.response',
  STATUS_UPDATE: 'com.cuca-gazelle.status.update',
  ERROR: 'com.cuca-gazelle.error',
  EVENT_PROCESSED: 'com.cuca-gazelle.event.processed',
  TRANSLATE: 'com.cuca-gazelle.translate',
  GENERATING_TRANSLATION: 'com.cuca-gazelle.generating-translation',
  TRANSLATE_RESPONSE: 'com.cuca-gazelle.translate.response',
  SUMMARY: 'com.cuca-gazelle.summary',
  GENERATING_SUMMARY: 'com.cuca-gazelle.generating-summary',
  SUMMARY_RESPONSE: 'com.cuca-gazelle.summary.response',
  SUMMARY_ERROR: 'com.cuca-gazelle.summary.error',
  SIMILARITY: 'com.cuca-gazelle.similarity',
  GENERATING_SIMILARITY: 'com.cuca-gazelle.generating-similarity',
  SIMILARITY_RESPONSE: 'com.cuca-gazelle.similarity.response',
  SIMILARITY_ERROR: 'com.cuca-gazelle.similarity.error',
  // Verbose middleware activity feed
  STATUS: 'com.cuca-gazelle.status',
  WORKFLOW_RETRY: 'com.cuca-gazelle.workflow.retry',
  WORKFLOW_FAILED: 'com.cuca-gazelle.workflow.failed',
  // Resumable-conversation segment control (FE -> worker)
  SEGMENT_SUMMARY: 'com.cuca-gazelle.segment.summary',
  SEGMENT_RESET: 'com.cuca-gazelle.segment.reset',
  SEGMENT_CONTINUE: 'com.cuca-gazelle.segment.continue',
  // Segment acknowledgements (worker -> FE)
  SEGMENT_RESET_ACK: 'com.cuca-gazelle.segment.reset.ack',
  SEGMENT_CONTINUE_ACK: 'com.cuca-gazelle.segment.continue.ack',
  // Dealer context (FE -> worker, ack worker -> FE)
  DEALER_INFO: 'com.cuca-gazelle.dealer.info',
  DEALER_INFO_ACK: 'com.cuca-gazelle.dealer.info.ack',
} as const;

// Base CloudEvent data interface with common fields
export interface BaseEventData {
  // Common fields that might appear across different event types
  timestamp?: string;
  event_count?: number;
  [key: string]: unknown;
}

// Specific CloudEvent Data Types
export interface ConnectionEventData extends BaseEventData {
  // Connection state
  status?: string;
  client_count?: number;
  session_restored?: boolean;

  // Session data included when restoring connection
  alarm_scheduled?: boolean;
  alarm_time?: string | null;
  pending_messages?: number;
  has_pending_messages?: boolean;
  last_event_time?: string | null;

  // Resumable-conversation segment state (echoed so a reload does not re-prompt)
  segment_mode?: string;
  boundary_ack?: boolean;
  acked_boundary_id?: string;
}

export interface ProcessingEventData extends BaseEventData {
  // Processing state
  message_count?: number;
  session_id?: string;
  timeout_seconds?: number;
}

export interface ErrorEventData extends BaseEventData {
  // Error information
  error_type?: string;
  error_message?: string;
}

export interface StatusUpdateEventData extends BaseEventData {
  // Legacy status updates
  status?: string;
}

export interface EventProcessedData extends BaseEventData {
  // Event acknowledgment
  status?: string;
  topic?: string;
}

// Translation request data
export interface TranslationRequestData {
  text: string;
  target: string;
  agent_language: string;
}

// Translation response data
export interface TranslationResponseData extends BaseEventData {
  data: string;
}

// Translation status data
export interface TranslationStatusData extends BaseEventData {
  status?: string;
}

// Summary request data
export type SummaryRequestData = BaseEventData;

// Summary status data
export interface SummaryStatusData extends BaseEventData {
  status?: string;
  session_id?: string;
  sessionId?: string;
  // True when this concerns a prior conversation segment (resume flow)
  previousSegment?: boolean;
}

// Summary response data
export interface SummaryResponseData extends BaseEventData {
  summary: string;
  session_id?: string;
  sessionId?: string;
  // True when this summary is for a prior segment (routes to the resume prompt)
  previousSegment?: boolean;
}

// Summary error data
export interface SummaryErrorData extends BaseEventData {
  error: string;
  session_id?: string;
  sessionId?: string;
  previousSegment?: boolean;
}

// Verbose middleware status data (activity feed)
export interface StatusEventData extends BaseEventData {
  phase: string;
  sessionId?: string;
  event_count?: number;
  timeout_seconds?: number;
}

// Workflow retry data (a previous attempt failed, retrying)
export interface WorkflowRetryData extends BaseEventData {
  attempt: number;
  max_attempts: number;
  next_attempt?: number;
  error?: string;
  workflow_type?: string;
}

// Workflow failed data (all attempts exhausted)
export interface WorkflowFailedData extends BaseEventData {
  attempts: number;
  error?: string;
  workflow_type?: string;
}

// Segment reset options (FE -> worker)
export interface SegmentResetData {
  keepSummaryAsContext: boolean;
  boundaryId: string;
}

// Similarity request data
export interface SimilarityRequestData {
  text: string;
  text_2: string;
}

// Similarity status data
export interface SimilarityStatusData extends BaseEventData {
  status?: string;
}

// Similarity response data
export interface SimilarityResponseData extends BaseEventData {
  similarity_score: number;
  sessionId?: string;
}

// Similarity error data
export interface SimilarityErrorData extends BaseEventData {
  error: string;
  sessionId?: string;
}

// For responses that come in format/response structure
export interface FormattedResponseData extends BaseEventData {
  format?: string;
  response?: string;
}

// Flexible type that can handle any event data structure
// This allows us to access any property safely with optional chaining
export interface BackendResponseData extends BaseEventData {
  // Connection events
  status?: string;
  client_count?: number;
  session_restored?: boolean;

  // Session restoration data
  alarm_scheduled?: boolean;
  alarm_time?: string | null;
  pending_messages?: number;
  has_pending_messages?: boolean;
  last_event_time?: string | null;

  // Processing events
  message_count?: number;
  session_id?: string;
  timeout_seconds?: number;

  // Error events
  error_type?: string;
  error_message?: string;

  // Event processed
  topic?: string;

  // Response events (includes BackendResponse fields)
  answer?: string;
  /** Answer text in the agent's language. */
  answer_agent_lang?: string;
  /** Deprecated aliases — kept for backward compat with older workflow responses. */
  answer_nl?: string;
  answer_agent?: string;
  input_language?: string;
  question?: string;
  /** Question text in the agent's language. */
  question_agent_lang?: string;
  /** Deprecated aliases. */
  question_nl?: string;
  question_agent?: string;
  bike_info?: {
    model: string;
    sku: string;
    snippet: string;
  };
  sources?: Array<{
    document_id: string;
    score: number;
    snippet: string;
    source: string;
  }>;
  articles?: Array<{
    category: string;
    link: string;
    score: number;
    summary: string;
    title: string;
  }>;

  // Format/response scenario
  format?: string;
  response?: string;

  // Summary response
  summary?: string;

  // Error handling
  error?: string;
}

// Specific CloudEvent Types
export type GenesysCloudEvent = CloudEvent<GenesysNotification>;
export type BackendCloudEvent = CloudEvent<BackendResponseData>;

// Type-specific CloudEvent types for better type safety (when needed)
export type ConnectionCloudEvent = CloudEvent<ConnectionEventData>;
export type ProcessingCloudEvent = CloudEvent<ProcessingEventData>;
export type ResponseCloudEvent = CloudEvent<BackendResponse>;
export type ErrorCloudEvent = CloudEvent<ErrorEventData>;
export type StatusUpdateCloudEvent = CloudEvent<StatusUpdateEventData>;
export type TranslationRequestCloudEvent = CloudEvent<TranslationRequestData>;
export type TranslationResponseCloudEvent = CloudEvent<TranslationResponseData>;
export type TranslationStatusCloudEvent = CloudEvent<TranslationStatusData>;
export type SummaryRequestCloudEvent = CloudEvent<SummaryRequestData>;
export type SummaryResponseCloudEvent = CloudEvent<SummaryResponseData>;
export type SummaryStatusCloudEvent = CloudEvent<SummaryStatusData>;
export type SummaryErrorCloudEvent = CloudEvent<SummaryErrorData>;
export type SimilarityRequestCloudEvent = CloudEvent<SimilarityRequestData>;
export type SimilarityResponseCloudEvent = CloudEvent<SimilarityResponseData>;
export type SimilarityStatusCloudEvent = CloudEvent<SimilarityStatusData>;
export type SimilarityErrorCloudEvent = CloudEvent<SimilarityErrorData>;

// Additional Information Structure
export interface AdditionalInfo {
  bicycle_details?: Record<string, unknown>;
  related_articles?: Array<{
    title: string;
    summary: string;
    link: string;
    category?: string;
    score?: number;
    source?: string; // Source name from the backend
  }>;
  suggestions?: string[];
}

// Genesys Integration Types
export interface GenesysNotification {
  topicName: string;
  eventTime: string;
  eventBody: {
    id: string;
    conversationId?: string;
    timestamp?: string;
    mediaType?: string;
    body?: string;
    direction?: MessageDirection;
    from?: {
      id: string;
      name?: string;
    };
    to?: {
      id: string;
      name?: string;
    };
    messengerType?: string;
  };
}

// Backend Communication Types
export interface BackendResponse {
  // AI response content
  answer?: string;
  /** Answer text in the agent's language. */
  answer_agent_lang?: string;
  /** Deprecated aliases — kept for backward compat with older workflow responses. */
  answer_nl?: string;
  answer_agent?: string;
  input_language?: string;
  question?: string;
  /** Question text in the agent's language. */
  question_agent_lang?: string;
  /** Deprecated aliases. */
  question_nl?: string;
  question_agent?: string;

  // Additional content
  bike_info?: {
    model: string;
    sku: string;
    snippet: string;
  };
  sources?: Array<{
    document_id: string;
    score: number;
    snippet: string;
    source: string;
  }>;
  articles?: {
    category: string;
    link: string;
    score: number;
    summary: string;
    title: string;
  }[];

  // Legacy fields
  translation?: string;
  original_message?: string;
  additional_info?: AdditionalInfo;

  // Error handling
  error?: string;

  // Format/response scenario - when backend returns data in this structure
  format?: string;
  response?: string;
}

export interface SessionStatus {
  // Basic Event Tracking
  event_count?: number;
  last_event_time?: string | null;

  // Alarm System (AI Response Trigger)
  alarm_scheduled?: boolean;
  alarm_time?: string | null;

  // WebSocket Connection State
  pending_messages?: number;
  has_pending_messages?: boolean;
}
